import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getMovieDetails,
  getMovieRecommendations,
  getMovieTrailerKey,
  getTrendingMovies,
} from '../services/tmdbService';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import DetailHeader from './Others/DetailHeader';
import '../styles/MovieDetails.css';

const SimilarCard = ({ item, type = 'movie' }) => {
  const title = item.title || item.name;
  const year = (item.release_date || item.first_air_date || '').substring(0, 4);
  const linkTo = type === 'movie' ? `/movie/${item.id}` : `/tv/${item.id}`;

  return (
    <Link to={linkTo} className="similar-card">
      <div className="similar-poster">
        {item.poster_path ? (
          <img
            src={`https://image.tmdb.org/t/p/w300${item.poster_path}`}
            alt={title}
            loading="lazy"
          />
        ) : (
          <div className="similar-poster-fallback">{title}</div>
        )}
        {typeof item.vote_average === 'number' && item.vote_average > 0 && (
          <span className="similar-rating">
            <i className="fa-solid fa-star" aria-hidden="true"></i>
            {item.vote_average.toFixed(1)}
          </span>
        )}
      </div>
      <p className="similar-card-title">{title}</p>
      {year && <p className="similar-card-year">{year}</p>}
    </Link>
  );
};

const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [trending, setTrending] = useState([]);
  const [error, setError] = useState(null);
  const [showTrailer, setShowTrailer] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const details = await getMovieDetails(id);
      setMovie(details);
      const [recs, trend] = await Promise.all([
        getMovieRecommendations(id).catch(() => []),
        getTrendingMovies(1).catch(() => []),
      ]);
      setRecommendations(recs.slice(0, 12));
      // Filter out the current movie from trending so it doesn't show next to itself
      setTrending(trend.filter((m) => String(m.id) !== String(id)).slice(0, 12));
    } catch {
      setError('Couldn’t load this movie. Please try again.');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="detail-page">
        <DetailHeader />
        <div className="detail-error">
          <p>{error}</p>
          <button type="button" onClick={load}>Try again</button>
          <button type="button" onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="detail-page">
        <DetailHeader />
        <div className="loading">Loading</div>
      </div>
    );
  }

  const {
    title,
    tagline,
    release_date,
    overview,
    backdrop_path,
    vote_average,
    runtime,
    genres,
    credits,
  } = movie;

  const trailerKey = getMovieTrailerKey(movie);
  const cast = (credits?.cast || []).slice(0, 8);
  const heroBg = backdrop_path
    ? `url(https://image.tmdb.org/t/p/original/${backdrop_path})`
    : 'none';

  const handlePlay = () => {
    addToRecentlyViewed({
      id: movie.id,
      title: movie.title,
      poster_path: movie.poster_path,
      type: 'movie',
    });
  };

  return (
    <div className="detail-page">
      <DetailHeader />

      <div className="detail-hero" style={{ backgroundImage: heroBg }}>
        <div className="detail-overlay" />
      </div>

      <div className="detail-content">
        <h1 className="detail-title">{title}</h1>
        {tagline && <p className="detail-tagline">&ldquo;{tagline}&rdquo;</p>}

        <div className="detail-meta-row">
          {typeof vote_average === 'number' && vote_average > 0 && (
            <span className="rating">
              <i className="fa-solid fa-star" aria-hidden="true"></i>
              {vote_average.toFixed(1)}
            </span>
          )}
          {release_date && <span>{release_date.substring(0, 4)}</span>}
          {runtime ? <span>{runtime} min</span> : null}
        </div>

        {genres && genres.length > 0 && (
          <div className="genres">
            {genres.slice(0, 3).map((g) => (
              <span key={g.id} className="genre-badge">{g.name}</span>
            ))}
          </div>
        )}

        <div className="detail-actions">
          <Link
            to={`/player/${id}?type=movie`}
            onClick={handlePlay}
            className="detail-play-button large"
          >
            <i className="fa-solid fa-play" aria-hidden="true"></i>
            <span>Play Now</span>
          </Link>
          {trailerKey && (
            <button
              type="button"
              className="detail-trailer-button"
              onClick={() => setShowTrailer(true)}
            >
              <i className="fa-solid fa-film" aria-hidden="true"></i>
              <span>Watch Trailer</span>
            </button>
          )}
        </div>

        {overview && <p className="detail-overview">{overview}</p>}

        {cast.length > 0 && (
          <div className="cast-section">
            <h3>Cast</h3>
            <div className="cast-row">
              {cast.map((actor) => (
                <div key={actor.id} className="cast-card">
                  <div className="cast-photo">
                    {actor.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                        alt={actor.name}
                        loading="lazy"
                      />
                    ) : (
                      <div className="cast-photo-fallback">
                        {actor.name?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  <p className="cast-name">{actor.name}</p>
                  {actor.character && (
                    <p className="cast-character">{actor.character}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {recommendations.length > 0 && (
          <div className="similar-section">
            <h2 className="similar-title">More Like This</h2>
            <div className="similar-grid">
              {recommendations.map((m) => (
                <SimilarCard key={m.id} item={m} type="movie" />
              ))}
            </div>
          </div>
        )}

        {trending.length > 0 && (
          <div className="similar-section">
            <h2 className="similar-title">Trending Now</h2>
            <div className="similar-grid">
              {trending.map((m) => (
                <SimilarCard key={m.id} item={m} type="movie" />
              ))}
            </div>
          </div>
        )}
      </div>

      {showTrailer && trailerKey && (
        <div
          className="trailer-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowTrailer(false)}
        >
          <div className="trailer-frame" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="trailer-close"
              onClick={() => setShowTrailer(false)}
              aria-label="Close trailer"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <iframe
              title={`${title} trailer`}
              src={`https://www.youtube-nocookie.com/embed/${trailerKey}?autoplay=1&rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieDetail;
